import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { filterServicesByOrderNumber } from 'src/entities/file/lib/service-table.utils';
import {
  DeleteOrderDialogResult,
  formatUserMessageText,
  getServiceOrderId,
  OrderService,
  resolveOrderGraphqlUserMessage,
  shouldRefreshAfterOrderError
} from 'src/entities/order';
import {
  isOrderStatusDeletable,
  getOrderMutationBlockedMessage,
  OrderStatus
} from 'src/entities/order/model/order-status.model';
import { UpdateOrderResult } from 'src/entities/order/model/update-order.model';

export interface DeleteServiceOrderDialogData {
  service: {
    nombreServicio?: string | null;
    providerSnapshot?: { nombre?: string | null };
    order?: {
      orderId?: string | null;
      orderNumber?: string | number | null;
      orderStatus?: string | null;
      reservationNumber?: string | null;
      reservationStatus?: string | null;
    };
  };
  dataAll?: any[];
  contextLabel?: string;
}

type ViewState = 'confirm' | 'loading' | 'error' | 'blocked';

@Component({
  selector: 'app-dialog-delete-os',
  templateUrl: './dialog-delete-os.component.html',
  styleUrls: ['./dialog-delete-os.component.css'],
  host: { class: 'delete-order-host' }
})
export class DialogDeleteOSComponent implements OnInit {
  state: ViewState = 'confirm';
  errorTitle: string | null = null;
  errorMessage: string | null = null;
  errorRequiresRefresh = false;

  readonly orderId: string | null;
  readonly orderNumber: string | number | null | undefined;
  readonly serviceName: string;
  readonly providerName: string;
  readonly orderStatus: string | null;
  readonly reservationNumber: string | null;
  readonly reservationStatus: string | null;
  readonly contextLabel: string | null;
  readonly affectedServicesCount: number;

  private deletedOrder: UpdateOrderResult | null = null;

  constructor(
    private readonly dialogRef: MatDialogRef<
      DialogDeleteOSComponent,
      DeleteOrderDialogResult
    >,
    @Inject(MAT_DIALOG_DATA) public data: DeleteServiceOrderDialogData,
    private readonly orderService: OrderService
  ) {
    this.orderId = getServiceOrderId(data.service as { order?: { orderId?: string | null } });
    this.orderNumber = data.service?.order?.orderNumber;
    this.serviceName = data.service?.nombreServicio?.trim() || 'Servicio';
    this.providerName = data.service?.providerSnapshot?.nombre?.trim() || '-';
    this.orderStatus = data.service?.order?.orderStatus ?? null;
    this.reservationNumber = data.service?.order?.reservationNumber ?? null;
    this.reservationStatus = data.service?.order?.reservationStatus ?? null;
    this.contextLabel = data.contextLabel ?? null;

    if (this.orderNumber && data.dataAll?.length) {
      const matches = filterServicesByOrderNumber(data.dataAll, this.orderNumber);
      this.affectedServicesCount = matches.length || 1;
    } else {
      this.affectedServicesCount = 1;
    }
  }

  ngOnInit(): void {
    if (!this.canDelete) {
      this.state = 'blocked';
    }
  }

  get canDelete(): boolean {
    return Boolean(this.orderId) && isOrderStatusDeletable(this.orderStatus);
  }

  get blockedMessage(): string {
    if (!this.orderId) {
      return 'Este servicio no tiene una Orden de Servicio asociada.';
    }

    return getOrderMutationBlockedMessage('eliminar', this.orderStatus);
  }

  get statusBadgeClass(): string {
    const status = this.orderStatus?.toUpperCase();
    if (status === OrderStatus.CONFIRMADO) return 'os-badge os-badge--confirmado';
    if (status === OrderStatus.EN_PROCESO) return 'os-badge os-badge--proceso';
    if (status === OrderStatus.PROCESADO) return 'os-badge os-badge--procesado';
    return 'os-badge';
  }

  confirmDelete(): void {
    if (!this.canDelete || this.state === 'loading') {
      return;
    }

    this.state = 'loading';
    this.errorTitle = null;
    this.errorMessage = null;
    this.errorRequiresRefresh = false;

    this.orderService.deleteOrder({ orderId: this.orderId! }).subscribe({
      next: (order) => {
        this.deletedOrder = order;
        this.close(true);
      },
      error: (error: unknown) => {
        const msg = resolveOrderGraphqlUserMessage(
          error,
          'No se pudo eliminar la Orden de Servicio.'
        );
        this.errorTitle = msg.title;
        this.errorMessage = formatUserMessageText(msg);
        this.errorRequiresRefresh = shouldRefreshAfterOrderError(error);
        this.state = 'error';
        if (this.errorRequiresRefresh) {
          this.dialogRef.disableClose = true;
        }
      }
    });
  }

  retry(): void {
    this.state = 'confirm';
    this.errorTitle = null;
    this.errorMessage = null;
    this.errorRequiresRefresh = false;
  }

  cancel(): void {
    this.close(false, false);
  }

  dismissBlocked(): void {
    this.close(false, false);
  }

  cerrarYActualizar(): void {
    this.dialogRef.disableClose = false;
    this.close(false, true);
  }

  private close(success: boolean, shouldRefresh = false): void {
    this.dialogRef.close({
      success,
      shouldRefresh,
      orderNumber: this.orderNumber,
      order: success ? this.deletedOrder : null
    });
  }
}
